import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'
import { RoleGuard } from '@shared/providers/src/MeProvider'
import { UserRole } from '@shared/utils'

import LinearProgress from '@components/LinearProgress'
import PaginationComp from '@components/Pagination'
import PatientAccessGuard from '@components/PatientAccessGuard'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'
import TableSortCell from '@components/TableSortCell'

import { useInsuranceCreation, useInsurances, useInsurancesFilters } from '../Insurances.hooks'
import InsuranceUpdate from '../InsuranceUpdate'
import InsuranceRow from './InsuranceRow'

const INLINE_COLUMNS = 6
const EXTENDED_COLUMNS = 8

export function Inline({ patientId, variant, perPage, disableRowsPerPage }) {
  const { data, isPending, isRefreshing } = useInsurancesList(patientId, perPage)

  return (
    <Stack spacing={1}>
      <Paper variant={variant} sx={{ overflow: 'hidden', position: 'relative' }}>
        <LinearProgress loading={isRefreshing} />
        <Content patientId={patientId} data={data} isLoading={isPending} perPage={perPage} />
      </Paper>
      <Pagination data={data} isLoading={isPending} perPage={perPage} disableRowsPerPage={disableRowsPerPage} />
    </Stack>
  )
}

export function Modal({ patientId, open, onClose }) {
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="paper">
      <ModalContent patientId={patientId} onClose={onClose} />
    </Dialog>
  )
}

function ModalContent({ patientId, onClose }) {
  const { data, isPending, isRefreshing } = useInsurancesList(patientId)

  return (
    <>
      <DialogTitle onClose={onClose}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography component="h2" variant="h4">
            Insurances
          </Typography>
          <AddInsuranceButton patientId={patientId} />
        </Stack>
      </DialogTitle>
      <LinearProgress loading={isRefreshing} />
      <DialogContent sx={{ p: 0 }}>
        <Content extended patientId={patientId} data={data} isLoading={isPending} />
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination data={data} isLoading={isPending} />
      </DialogActions>
    </>
  )
}

function useInsurancesList(patientId, perPageOverride) {
  const [page, queryPerPage] = usePageParams({ id: 'insurances' })
  const perPage = perPageOverride ?? queryPerPage

  const [{ insurancesSort }] = useInsurancesFilters()
  return useInsurances(
    patientId,
    { order: insurancesSort, limit: perPage, offset: (page - 1) * perPage },
    { placeholderData: keepPreviousData, enabled: Boolean(patientId) }
  )
}

function Pagination({ data, isLoading, perPage: perPageOverride, disableRowsPerPage }) {
  const [page, queryPerPage, setPageParams] = usePageParams({ id: 'insurances' })
  const perPage = perPageOverride ?? queryPerPage

  return (
    <PaginationComp
      id="insurances"
      loading={isLoading}
      last={data?.length < perPage}
      page={page}
      perPage={perPage}
      setPageParams={(params) => setPageParams(perPageOverride ? { page: params.page } : params)}
      disableRowsPerPage={disableRowsPerPage}
    />
  )
}

export function AddInsuranceButton({ patientId, size }) {
  const [open, setOpen] = useState(false)
  const create = useInsuranceCreation(patientId)

  return (
    <RoleGuard allowed={[UserRole.Admin, UserRole.MA, UserRole.Support, UserRole.Provider, UserRole.CBO]}>
      <PatientAccessGuard patientId={patientId}>
        <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
          <Button
            size={size}
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
          >
            Add Insurance
          </Button>
        </Stack>
        <InsuranceUpdate onUpdate={create.mutateAsync} open={open} onClose={() => setOpen(false)} />
      </PatientAccessGuard>
    </RoleGuard>
  )
}

function Content({ patientId, data, isLoading, extended = false, perPage }) {
  const [{ insurancesSort }, updateFilters] = useInsurancesFilters()

  const columns = extended ? EXTENDED_COLUMNS : INLINE_COLUMNS
  const showLoading = isLoading
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <TableContainer>
      <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            {extended && <TableCell>ID</TableCell>}
            <TableCell>Insurance Provider / Subscriber</TableCell>
            <TableCell>ID / Group</TableCell>
            {extended && <TableCell>BIN / PCN</TableCell>}
            <TableSortCell direction={insurancesSort} onChange={(insurancesSort) => updateFilters({ insurancesSort })}>
              Date
            </TableSortCell>
            <TableCell>Status</TableCell>
            <RoleGuard allowed={[UserRole.Admin, UserRole.MA, UserRole.Support, UserRole.Provider]}>
              <PatientAccessGuard patientId={patientId}>
                <TableCell align="right">Actions</TableCell>
              </PatientAccessGuard>
            </RoleGuard>
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoading && <TableLoader columns={columns} items={perPage ?? 2} />}
          {showEmpty && <TableEmpty columns={columns} />}
          {showData &&
            data.map((insurance) => (
              <InsuranceRow key={insurance.id} patientId={patientId} data={insurance} columns={columns} extended={extended} />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
